import { CSSProperties, useId } from 'react';

import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { useTheme } from '@mui/material';

export type MeshBlob = Readonly<{
  color: ColorToken;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  width: string;
  height: string;
  /** SVG path `d`, drawn in a 100×100 viewBox and stretched to width/height. */
  shape: string;
  rotate: number;
}>;

export type MeshConfig = Readonly<{
  baseColor: ColorToken;
  blobs: readonly MeshBlob[];
  blurPx?: number;
  grainOpacity?: number;
}>;

// Inline SVG noise — generated in-browser, no network fetch.
const GRAIN_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240">
     <filter id="n">
       <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/>
       <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0"/>
     </filter>
     <rect width="100%" height="100%" filter="url(#n)"/>
   </svg>`,
)}`;

// everything stays inside one card-sized <svg> — boxes that overhang the card inflate drag ghosts
export const MeshGradient = ({ config }: { config: MeshConfig }) => {
  const { backrs } = useTheme();
  const { baseColor, blobs, blurPx = 30, grainOpacity = 0.35 } = config;
  const filterId = `mesh-blur-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <div style={containerStyle(backrs.colors[baseColor] as string)}>
      <svg width="100%" height="100%" style={meshSvgStyle} aria-hidden="true">
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={blurPx} />
          </filter>
        </defs>
        <g filter={`url(#${filterId})`}>
          {blobs.map((blob, i) => (
            <svg
              key={i}
              {...blobPlacement(blob)}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              overflow="visible"
              style={blobRotationStyle(blob.rotate)}
            >
              <path d={blob.shape} fill={backrs.colors[blob.color] as string} />
            </svg>
          ))}
        </g>
      </svg>
      <div style={grainStyle(grainOpacity)} />
    </div>
  );
};

// blob geometry is authored relative to a layer 150% of card size, offset -25%
const blobPlacement = (blob: MeshBlob) => {
  const scale = (value: string | undefined) => parseFloat(value ?? '0%') * 1.5;
  const width = scale(blob.width);
  const height = scale(blob.height);
  const x = blob.left !== undefined ? -25 + scale(blob.left) : 125 - scale(blob.right) - width;
  const y = blob.top !== undefined ? -25 + scale(blob.top) : 125 - scale(blob.bottom) - height;

  return { x: `${x}%`, y: `${y}%`, width: `${width}%`, height: `${height}%` };
};

const blobRotationStyle = (rotate: number): CSSProperties => ({
  transform: `rotate(${rotate}deg)`,
  transformOrigin: 'center',
  transformBox: 'fill-box',
});

const containerStyle = (baseColor: string): CSSProperties => ({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  backgroundColor: baseColor,
  pointerEvents: 'none',
});

const meshSvgStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
};

const grainStyle = (opacity: number): CSSProperties => ({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url("${GRAIN_SVG}")`,
  backgroundSize: '240px 240px',
  mixBlendMode: 'overlay',
  opacity,
});
